import { getAddressFieldsConfig } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { AddressField } from 'constants/loanApplication';
import { getConditionalObject } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

const GRID_FULL = 12;
const GRID_LG = 9;

export const CREATE_NEW_CONTACT_TYPE_OPTION = {
  name: 'Create New Contact Type',
  value: 'create-new-contact-type',
};

const contactsConfigFields = {
  contactTypeId: {
    fieldId: 'contactTypeId',
    type: FieldType.SELECT,
    nameInModel: 'contactTypeId',
    inputProps: {
      placeholder: 'Type',
      label: 'Type',
      required: true,
      options: [],
      withSortedOptions: true,
    },
  },
  firstName: {
    fieldId: 'firstName',
    type: FieldType.INPUT,
    nameInModel: 'firstName',
    inputProps: {
      placeholder: 'First Name',
      label: 'First Name',
      required: true,
    },
  },
  middleName: {
    fieldId: 'middleName',
    type: FieldType.INPUT,
    nameInModel: 'middleName',
    inputProps: {
      placeholder: 'Middle Name',
      label: 'Middle Name',
      required: false,
    },
  },
  lastName: {
    fieldId: 'lastName',
    type: FieldType.INPUT,
    nameInModel: 'lastName',
    inputProps: {
      placeholder: 'Last Name',
      label: 'Last Name',
      required: true,
    },
  },
  companyName: {
    fieldId: 'companyName',
    type: FieldType.INPUT,
    nameInModel: 'companyName',
    inputProps: {
      placeholder: 'Company Name',
      label: 'Company Name',
      required: false,
    },
  },
  nmlsId: {
    fieldId: 'nmlsId',
    type: FieldType.INPUT,
    nameInModel: 'nmlsId',
    inputProps: {
      placeholder: 'NMLS ID',
      label: 'NMLS ID',
      required: false,
      maxLength: 20,
    },
  },
  stateLicenseId: {
    fieldId: 'stateLicenseId',
    type: FieldType.INPUT,
    nameInModel: 'stateLicenseId',
    inputProps: {
      placeholder: 'State License',
      label: 'State License',
      required: false,
      maxLength: 50,
    },
  },
  email: {
    fieldId: 'email',
    type: FieldType.INPUT,
    nameInModel: 'email',
    inputProps: {
      placeholder: 'Email',
      label: 'Email',
      required: false,
      type: FieldInputType.EMAIL,
      columns: 3,
    },
  },
  phoneNumber: {
    fieldId: 'phoneNumber',
    type: FieldType.TEL,
    nameInModel: 'phoneNumber',
    inputProps: {
      placeholder: 'Phone',
      label: 'Phone',
      required: false,
      type: FieldInputType.TEL,
      columns: 2,
    },
  },
  phoneNumberExtension: {
    fieldId: 'phoneNumberExtension',
    type: FieldType.INPUT,
    nameInModel: 'phoneNumberExtension',
    inputProps: {
      placeholder: 'Extension',
      label: '',
      required: false,
      type: FieldInputType.EXT,
      maxLength: 6,
      columns: 1,
    },
  },
  directLine: {
    fieldId: 'directLine',
    type: FieldType.TEL,
    nameInModel: 'directLine',
    inputProps: {
      placeholder: 'Direct Line',
      label: 'Direct Line',
      required: false,
      type: FieldInputType.TEL,
      columns: 3,
    },
  },
};

const addressFieldsToPasteInConfig = [
  AddressField.STREET,
  AddressField.UNIT,
  AddressField.CITY,
  AddressField.SUITE,
  AddressField.STATE,
  AddressField.ZIP,
];

const getAddressFields = (prefix = '') =>
  Object.values(
    getAddressFieldsConfig({
      prefix,
      isWithCounty: false,
      isWithCountry: false,
      isAllFieldsNonRequired: true,
    }),
  ).reduce((acc, addressField) => {
    if (addressFieldsToPasteInConfig.includes(addressField.fieldId)) {
      if (addressField.fieldId === AddressField.STREET) {
        addressField.dependentFields = addressField.dependentFields.filter(({ nameInSuggestion }) =>
          addressFieldsToPasteInConfig.includes(nameInSuggestion),
        );
      }

      if (addressField.fieldId === AddressField.UNIT) {
        addressField.inputProps.label = 'Suite';
        addressField.inputProps.placeholder = 'Suite';
        addressField.fieldId = AddressField.SUITE;
        addressField.nameInModel = `[${prefix}].${AddressField.SUITE}`;
      }

      acc.push(addressField);
    }

    return acc;
  }, []);

const addressGroupKey = 'company-address';

// A Loan Origination Company contact is a company: it has no person name, email or phone, and needs a company name.
const withRequired = (field, required) => ({
  ...field,
  inputProps: { ...field.inputProps, required },
});

export const contactsFieldsKeys = [
  ...Object.keys({ ...contactsConfigFields }),
  ...getAddressFields().map(({ nameInModel }) => nameInModel),
];

export const getContactsConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    contactId,
    contactTypesOptions,
    numberOfColumns,
    isEmail,
    isPhoneNumber,
    isCompanyContact,
  }) => {
    const groups = [
      {
        type: FieldType.GROUP,
        key: 'contacts-general',
        fields: [
          {
            ...contactsConfigFields.contactTypeId,
            inputProps: {
              ...contactsConfigFields.contactTypeId.inputProps,
              options: contactTypesOptions,
            },
          },
        ],
      },
      {
        type: FieldType.GROUP,
        key: 'contacts-name',
        fields: [
          withRequired(contactsConfigFields.firstName, !isCompanyContact),
          contactsConfigFields.middleName,
          withRequired(contactsConfigFields.lastName, !isCompanyContact),
        ],
      },
      {
        type: FieldType.GROUP,
        key: 'contacts-company',
        fields: [
          withRequired(contactsConfigFields.companyName, isCompanyContact),
          contactsConfigFields.nmlsId,
          contactsConfigFields.stateLicenseId,
        ],
      },
      {
        type: FieldType.GROUP,
        key: addressGroupKey,
        fields: getAddressFields(`[${contactId}].address`),
        lockAsGroup: true,
      },
      {
        type: FieldType.GROUP,
        key: 'contacts-contacts',
        customStyles: {
          gridTemplateColumns: `repeat(${
            numberOfColumns === GRID_FULL ? GRID_FULL : GRID_LG
          }, 1fr)`,
          alignItems: 'end',
        },
        fields: [
          withRequired(contactsConfigFields.email, !isCompanyContact && !isPhoneNumber),
          withRequired(contactsConfigFields.phoneNumber, !isCompanyContact && !isEmail),
          contactsConfigFields.phoneNumberExtension,
          contactsConfigFields.directLine,
        ],
      },
    ].map(group => {
      return {
        ...group,
        key: `${contactId}.${group.key}`,
        ...getConditionalObject(group.key !== addressGroupKey, () => ({
          fields: group.fields.map(field => ({
            ...field,
            nameInModel: `${contactId}.${field.nameInModel}`,
          })),
        })),
      };
    });

    return {
      groups,
    };
  },
);
